import { useRef, useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Icon } from '@iconify/react';
import bellFill from '@iconify/icons-eva/bell-fill';
import {
  Box,
  List,
  Badge,
  Button,
  IconButton,
  Divider,
  Typography,
  ListSubheader,
  ListItem,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  useTheme,
} from '@mui/material';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import Scrollbar from 'components/Scrollbar';
import MenuPopover from 'components/MenuPopover';
import { MIconButton } from 'components/@material-extend';
import { useTranslation } from 'react-i18next';
import { useGetNotificationUnreadCount, useGetNotifications } from 'modules/notification/api/queries';
import { useDeleteNotificationAll, useMarkNotificationAsRead } from 'modules/notification/api/mutation';
import NotificationCard from 'modules/notification/components/notificationCard';
import { PATH_DASHBOARD } from 'routes/paths';
import NoData from 'components/NoData';
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog';

export default function NotificationsPopover() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation('notification');

  const displayName = (raw?: string | null) =>
    raw === 'system' || !raw ? t('system_display') : raw;

  const { data: unreadCountData } = useGetNotificationUnreadCount();
  const { mutateAsync: deleteAllMutation } = useDeleteNotificationAll();
  const { mutateAsync: markAsReadMutation } = useMarkNotificationAsRead();

  const [selectedFarmId, setSelectedFarmId] = useState<string>('all');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const params =
    selectedFarmId === 'all' ? undefined : selectedFarmId === 'system' ? 'system' : String(selectedFarmId);

  const { data: notificationsData, isLoading, isFetching, refetch } = useGetNotifications(params, page, pageSize);

  const [localNotifications, setLocalNotifications] = useState<any[]>([]);

  useEffect(() => {
    if (!notificationsData) return;

    setLocalNotifications((prev) => {
      if (page === 1) {
        return notificationsData.data ?? [];
      }
      const newItems = (notificationsData.data ?? []).filter(
        (n) => !prev.some((p) => String(p.id) === String(n.id))
      );
      return [...prev, ...newItems];
    });
  }, [notificationsData, page]);

  const markAsRead = useCallback(
    async (id: string | number) => {
      try {
        await markAsReadMutation([String(id)]);
      } catch (e) { }
      finally {
        setLocalNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, read_at: n.read_at ?? new Date().toISOString() } : n))
        );
      }
    },
    [markAsReadMutation]
  );

  const observerRef = useRef<IntersectionObserver | null>(null);
  const timersRef = useRef<Record<string, number>>({});
  const observedElsRef = useRef(new Map<Element, string | number>());

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = observedElsRef.current.get(entry.target);
          if (!id) return;
          const notif = localNotifications.find((n) => String(n.id) === String(id));
          if (!notif || notif.read_at) {
            observerRef.current?.unobserve(entry.target);
            observedElsRef.current.delete(entry.target);
            const t = timersRef.current[String(id)];
            if (t) {
              clearTimeout(t);
              delete timersRef.current[String(id)];
            }
            return;
          }

          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            if (!timersRef.current[String(id)]) {
              timersRef.current[String(id)] = window.setTimeout(() => {
                markAsRead(id);
                delete timersRef.current[String(id)];
                observerRef.current?.unobserve(entry.target);
                observedElsRef.current.delete(entry.target);
              }, 2000);
            }
          } else {
            const t = timersRef.current[String(id)];
            if (t) {
              clearTimeout(t);
              delete timersRef.current[String(id)];
            }
          }
        });
      },
      { threshold: [0.5] }
    );

    return () => {
      observerRef.current?.disconnect();
      Object.values(timersRef.current).forEach((t) => clearTimeout(t));
      timersRef.current = {};
      observedElsRef.current.clear();
    };
  }, [markAsRead, localNotifications]);

  const attachObserverFor = useCallback(
    (el: Element | null, id: string | number, readAt?: string | null) => {
      if (!observerRef.current) return;
      if (!el) {
        for (const [key, val] of observedElsRef.current.entries()) {
          if (String(val) === String(id)) {
            observerRef.current.unobserve(key);
            observedElsRef.current.delete(key);
            break;
          }
        }
        return;
      }
      if (readAt) return;
      observedElsRef.current.set(el, id);
      observerRef.current.observe(el);
    },
    []
  );

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      try {
        const deleteParam =
          selectedFarmId === 'all'
            ? undefined
            : selectedFarmId === 'system'
              ? 'system'
              : String(selectedFarmId);

        await deleteAllMutation(deleteParam as any);
      } catch (e) { }
    },
  });

  const handleOpen = () => {
    setSelectedFarmId('all');
    setPage(1);
    setOpen(true);
    refetch();
  };
  const handleClose = () => setOpen(false);

  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: notificationsData?.nextPage !== null,
    onLoadMore: () => setPage((prev) => prev + 1),
    rootMargin: '0px 0px 200px 0px',
  });

  return (
    <>
      <MIconButton
        ref={anchorRef}
        size="large"
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
      >
        <Badge
          badgeContent={unreadCountData?.total_unread}
          color="error"
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiBadge-badge': {
              transform: 'translate(70%, -70%)',
              fontSize: 12,
              lineHeight: '18px',
              boxShadow: '0 0 0 2px ' + (theme.palette.background.paper || '#fff'),
            },
          }}
        >
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <img src="/static/icons/layout/bell-notification.svg" width={20} height={20} alt="notifications" />
          </Box>
        </Badge>
      </MIconButton>

      <MenuPopover open={open} onClose={handleClose} anchorEl={anchorRef.current} sx={{ width: 380, maxWidth: '100%', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <IconButton
            onClick={handleClose}
            disableRipple
            component={RouterLink}
            to={PATH_DASHBOARD.notification.setting}
          >
            <Icon icon="ant-design:setting-outlined" width="24" height="24" />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('title')}</Typography>
          <IconButton size="small" disabled={isLoading} onClick={openConfirmDialog}>
            <Icon icon="mingcute:delete-2-line" width="24" height="24" />
          </IconButton>
        </Box>

        <Divider />

        <Scrollbar sx={{ maxHeight: { xs: '60vh', sm: '60vh' }, overflow: 'auto' }}>
          <FormControl size="small" sx={{ my: 2, width: '100%' }}>
            <InputLabel id="notification-filter-label">{t('filter')}</InputLabel>
            <Select
              labelId="notification-filter-label"
              value={selectedFarmId}
              label={t('filter')}
              onChange={(e) => {
                const value = e.target.value as string;
                setSelectedFarmId(value);
                setPage(1);
                setLocalNotifications([]);
              }}
            >
              <MenuItem value="all">{t('all')}</MenuItem>
              {unreadCountData?.items.map((opt: any) => {
                const value = opt.name === 'system' ? 'system' : String(opt.id);
                return (
                  <MenuItem key={opt.id ?? opt.name} value={value}>
                    {`${displayName(opt.name)} (${opt.unread})`}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {localNotifications.length === 0 && isLoading && (
            <Box sx={{ p: 2 }}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={70} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          )}

          {!isLoading && localNotifications.length === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
              <img src="/static/icons/ci_no_data.svg" alt="" />
              <Typography color="text.secondary" variant="h6">
                {t('no_notifications')}
              </Typography>
            </Box>
          )}

          {localNotifications.length > 0 && (
            <>
              <List disablePadding>
                {localNotifications.map((n) => (
                  <ListItem
                    sx={{ mb: 2 }}
                    key={n.id}
                    disablePadding
                    ref={(el) => attachObserverFor(el, n.id, n.read_at ?? null)}
                  >
                    <NotificationCard
                      farm_name={n.farm_id ? n.farm_name : t('system_display')}
                      title={n.title}
                      body={n.body}
                      created_at={n.created_at}
                      read_at={n.read_at ?? null}
                      lines={2}
                    />
                  </ListItem>
                ))}
              </List>

              {isFetching && localNotifications.length > 0 && (
                <Box sx={{ p: 1 }}>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                </Box>
              )}

              {notificationsData?.nextPage && <div ref={sentryRef} />}
            </>
          )}
        </Scrollbar>

        <Divider />

        <Box sx={{ pt: 2 }}>
          <Button
            variant="contained"
            onClick={handleClose}
            fullWidth
            disableRipple
            component={RouterLink}
            to={PATH_DASHBOARD.notification.root}
          >
            {t('viewAll')}
          </Button>
        </Box>
        {dialog}
      </MenuPopover>
    </>
  );
}
